"use client";
import { Box, Button, Container, Typography } from "@mui/material";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useApp } from "@/providers/AppProvider";
import { useTelegram } from "@/providers/TelegramProvider";
import env from "@/utils/config";
import { Terms } from "@/components/layouts/Terms";
import { Dashboard } from "@/components/layouts/Dashboard";
import GradientLogoLoading from "@/components/layouts/GradientLogoLoading";

export default function HomePage() {
	const { isLoading } = useApp();
	const { rawInitData, user } = useTelegram();
	const isAuth = !!user;
	const router = useRouter();
	const [showTerms, setShowTerms] = useState(false);
	const [showContent, setShowContent] = useState(false);

	useEffect(() => {
		if (!isLoading) {
			const timer = setTimeout(() => {
				setShowContent(true);
			}, 500);
			return () => clearTimeout(timer);
		}
	}, [isLoading]);

	const startParam = useMemo(() => {
		if (!rawInitData) return null;
		const params = new URLSearchParams(rawInitData);
		return params.get("start_param");
	}, [rawInitData]);

	const parseStartParam = (startParam: string): string => {
		if (!startParam) return "/";

		console.log("Parsing start param:", startParam);

        // Если параметр короткий (например, реферальный код), то ничего не делаем (остаемся на главной)
        // Реферальный код обрабатывается на бэкенде при авторизации
        if (startParam.length <= 10 && !startParam.includes('=')) {
             console.log("Start param looks like a referral code, staying on home page");
             return "/";
        }

		try {
			const decoded = atob(startParam);
			console.log("Decoded start param:", decoded);

			const data = JSON.parse(decoded);
			console.log("Parsed start param data:", data);

			const path = data.path || "";
			const queryParams = new URLSearchParams(
				data.params || {},
			).toString();
			const result = queryParams ? `/${path}?${queryParams}` : `/${path}`;

			console.log("Final redirect path:", result);
			return result;
		} catch (error) {
			console.error("Error parsing start param:", error);
			console.log("Using start param as simple path");
			return `/${startParam}`;
		}
	};

	const performRedirect = (redirectPath: string) => {
        if (redirectPath === "/") return; // Не редиректим на главную, если мы уже там
		console.log("Executing redirect to:", redirectPath);
		setTimeout(() => {
			router.push(redirectPath);
		}, 50);
	};

	useEffect(() => {
		if (!isLoading && user && startParam) {
			const redirectPath = parseStartParam(startParam);
			performRedirect(redirectPath);
		}
	}, [isLoading, user, startParam, router]);

	const userButton = () => {
		if (showTerms) {
			return <Terms onClose={() => setShowTerms(false)} />;
		}

		if (isAuth) {
			return <Dashboard />;
		} else {
			return (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexDirection: "column",
						gap: 2,
						width: "100%",
					}}
				>
					<Typography>
						Этот сайт работает только через Telegram
					</Typography>
					<Button
						size="medium"
						variant="contained"
						onClick={() => {
							window.open(
								`${env.PUBLIC_TG_BOT_URL}/${env.PUBLIC_TG_BOT_USERNAME?.split("_bot")?.[0].toLowerCase()}`,
								"_blank",
							);
						}}
					>
						Открыть в Telegram
					</Button>
					<Button
						onClick={() => setShowTerms(true)}
						sx={{
							mt: 2,
							color: "text.secondary",
							fontSize: "0.75rem",
							textTransform: "none",
						}}
						variant="text"
					>
						Условия и политика конфиденциальности
					</Button>
				</Box>
			);
		}
	};

	return (
		<Box sx={{ width: "100%" }}>
			<Box
				sx={{
					opacity: showContent ? 1 : 0,
					transform: showContent
						? "translateY(0)"
						: "translateY(30vh)",
					transition:
						"opacity 1s ease-out, transform 1s cubic-bezier(0.2, 0.8, 0.2, 1)",
				}}
			>
				<Container
					maxWidth="sm"
					sx={{
						display: "flex",
						flexDirection: "column",
						justifyContent: "flex-start",
						flex: 1,
						paddingTop: "160px",
						height:'100vh',
					}}
				>
					{userButton()}
				</Container>
			</Box>
		</Box>
	);
}
